import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { EVENT_COLOR_PALETTE } from '../../services/calendar/eventColors';
import { cn } from '../../lib/cn';

/** Pastille de couleur cliquable : une palette, et une couleur libre pour qui veut la sienne. */
export function ColorSwatchPicker({
  value,
  onChange,
  label,
  disabled,
  onReset,
  resetLabel,
}: {
  /** null : aucune couleur choisie (pastille vide). */
  value: string | null;
  onChange: (color: string) => void;
  label: string;
  disabled?: boolean;
  /** Présent : un lien sous la palette pour retirer la couleur. */
  onReset?: () => void;
  resetLabel?: string;
}) {
  const [open, setOpen] = useState(false);

  function pick(color: string) {
    onChange(color);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={label}
          aria-label={label}
          className={cn(
            'h-7 w-7 flex-shrink-0 rounded-full border transition-transform hover:scale-105 disabled:opacity-50',
            value ? 'border-black/10' : 'border-dashed border-neutral-300 bg-white',
          )}
          style={value ? { background: value } : undefined}
        />
      </PopoverTrigger>
      <PopoverContent align="start" data-cime-modal className="w-[196px] rounded-xl border-neutral-200 bg-white p-3">
        <div className="grid grid-cols-5 gap-2">
          {EVENT_COLOR_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => pick(color)}
              aria-label={color}
              aria-pressed={value?.toLowerCase() === color}
              className={cn(
                'h-7 w-7 rounded-full border border-black/10 transition-transform hover:scale-110',
                value?.toLowerCase() === color && 'ring-2 ring-cime-primary ring-offset-2',
              )}
              style={{ background: color }}
            />
          ))}
        </div>
        <label className="mt-3 flex items-center justify-between gap-2 text-xs text-cime-text-muted">
          Autre couleur
          <input
            type="color"
            value={value ?? '#64748b'}
            onChange={(e) => onChange(e.target.value)}
            className="h-7 w-10 cursor-pointer rounded border border-neutral-200 bg-white p-0.5"
          />
        </label>
        {onReset ? (
          <button
            type="button"
            onClick={() => {
              onReset();
              setOpen(false);
            }}
            className="mt-2 text-xs font-medium text-cime-primary hover:underline"
          >
            {resetLabel ?? 'Retirer la couleur'}
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
